// Shared helpers for the app's date string formats.
//
// Display format: `MM/DD` or `MM/DD/YYYY` (two-digit years are interpreted as
// 20YY). Two-part dates default to the current year — fine for short-horizon
// comparisons but be aware of the year-rollover edge case across Dec/Jan.
//
// Backend (v1 API) format: `YYYY-MM-DD`.

// Parses a display-format date string to a Date with no time component, or
// null when the input is blank/unparseable.
export function parseMdYDate(s: string | undefined | null): Date | null {
  if (!s || !s.trim()) return null;
  const parts = s.trim().split('/');
  let m: number, d: number, y: number;
  if (parts.length === 2) {
    m = parseInt(parts[0], 10);
    d = parseInt(parts[1], 10);
    y = new Date().getFullYear();
  } else if (parts.length === 3) {
    m = parseInt(parts[0], 10);
    d = parseInt(parts[1], 10);
    const rawYear = parts[2];
    y = rawYear.length === 2 ? 2000 + parseInt(rawYear, 10) : parseInt(rawYear, 10);
  } else {
    return null;
  }
  if (!Number.isFinite(m) || !Number.isFinite(d) || !Number.isFinite(y)) return null;
  return new Date(y, m - 1, d);
}

// Converts a display-format date string to backend YYYY-MM-DD. Already-ISO
// inputs pass through unchanged. Returns null when blank/unparseable.
export function formatBackendDate(s: string | undefined | null): string | null {
  if (!s) return null;
  const trimmed = String(s).trim();
  if (!trimmed) return null;
  if (/^\d{4}-\d{2}-\d{2}$/.test(trimmed)) return trimmed;
  const parts = trimmed.split('/');
  let m: string, d: string, y: string;
  if (parts.length === 2) {
    [m, d] = parts;
    y = String(new Date().getFullYear());
  } else if (parts.length === 3) {
    [m, d] = parts;
    const rawYear = parts[2];
    y = rawYear.length === 2 ? `20${rawYear}` : rawYear;
  } else {
    return null;
  }
  return `${y}-${m.padStart(2, '0')}-${d.padStart(2, '0')}`;
}

// Convert a leg's display-format appt date (MM/DD or MM/DD/YY, as produced by
// SHARED_ORDER_FIELDS in orders.py) into YYYY-MM-DD for the dispatch payload.
// MM/DD assumes the current year — matches how the backend itself formats
// same-year dates. Returns null on unparseable input so callers can drop the
// field rather than send garbage.
export function legDateToIso(input: string | null | undefined): string | null {
  const s = (input ?? '').trim();
  if (!s || s === '-' || s === '0000-00-00') return null;
  // Catch zero-MM/DD sentinels DATE_FORMAT can produce when the column is
  // "0000-00-00" but SHARED_ORDER_FIELDS's `= 0 OR IS NULL` guard missed it.
  if (/^0+\/0+(\/0+)?$/.test(s)) return null;
  const parts = s.split('/');
  if (parts.length === 2) {
    const [m, d] = parts;
    if (!m || !d) return null;
    const year = new Date().getFullYear();
    return `${year}-${m.padStart(2, '0')}-${d.padStart(2, '0')}`;
  }
  if (parts.length === 3) {
    const [m, d, y] = parts;
    if (!m || !d || !y) return null;
    const year = y.length === 2 ? 2000 + parseInt(y, 10) : parseInt(y, 10);
    if (!Number.isFinite(year)) return null;
    return `${year}-${m.padStart(2, '0')}-${d.padStart(2, '0')}`;
  }
  return null;
}

// Date → "MM/DD/YYYY". Inverse of parseMdYDate. Blank for null.
export function formatMdYDate(d: Date | null | undefined): string {
  if (!d) return '';
  const m = (d.getMonth() + 1).toString().padStart(2, '0');
  const day = d.getDate().toString().padStart(2, '0');
  return `${m}/${day}/${d.getFullYear()}`;
}

// "HH:MM" (24h) or "H:MM AM/PM" (12h) → Date with today's date + parsed time.
// Returns null when blank/unparseable.
export function parseHmTime(s: string | null | undefined): Date | null {
  if (!s || !s.trim()) return null;
  const m24 = s.trim().match(/^(\d{1,2}):(\d{2})$/);
  if (m24) {
    const d = new Date();
    d.setHours(parseInt(m24[1], 10), parseInt(m24[2], 10), 0, 0);
    return d;
  }
  const m12 = s.trim().match(/^(\d{1,2}):(\d{2})\s*(AM|PM)$/i);
  if (m12) {
    let h = parseInt(m12[1], 10);
    const min = parseInt(m12[2], 10);
    const pm = m12[3].toUpperCase() === 'PM';
    if (pm && h !== 12) h += 12;
    if (!pm && h === 12) h = 0;
    const d = new Date();
    d.setHours(h, min, 0, 0);
    return d;
  }
  return null;
}

// Date → "HH:MM" (24h). Blank for null.
export function formatHmTime(d: Date | null | undefined): string {
  if (!d) return '';
  return `${d.getHours().toString().padStart(2, '0')}:${d.getMinutes().toString().padStart(2, '0')}`;
}

// "MM/DD/YYYY HH:MM" → Date. Returns null when blank/unparseable.
export function parseMdYHm(s: string | null | undefined): Date | null {
  if (!s || !s.trim()) return null;
  const parts = s.trim().split(/\s+/);
  if (parts.length !== 2) return null;
  const d = parseMdYDate(parts[0]);
  if (!d) return null;
  const t = parts[1].split(':');
  if (t.length < 2) return null;
  d.setHours(parseInt(t[0], 10), parseInt(t[1], 10), 0, 0);
  return d;
}

// Date → "MM/DD/YYYY HH:MM". Inverse of parseMdYHm. Blank for null.
export function formatMdYHm(d: Date | null | undefined): string {
  if (!d) return '';
  return `${formatMdYDate(d)} ${formatHmTime(d)}`;
}

// History-grid timestamp, e.g. "07/10/2026, 07:20:35" (24h, en-US). Blank for
// null. Shared by the Pro History drawer sub-panels.
export function formatHistoryDateTime(isoString: string | null | undefined): string {
  if (!isoString) return '';
  const date = new Date(isoString);
  return date.toLocaleString('en-US', {
    month: '2-digit',
    day: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: false,
  });
}

// Date-only fields arrive as ISO dates (YYYY-MM-DD); reformat to MM/DD/YYYY
// without going through Date() so no timezone shift can move the day.
export function formatIsoDateOnly(isoDate: string | null | undefined): string {
  if (!isoDate) return '';
  const datePart = isoDate.split('T')[0];
  const m = datePart.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (m) return `${m[2]}/${m[3]}/${m[1]}`;
  return datePart;
}

// Long-form local datetime, e.g. "4/27/2026 3:45 PM". Accepts any string the
// Date constructor understands (ISO, backend datetime). Returns null when
// blank/unparseable so callers can fall back. Used for "last performed" / "last
// event" style notices.
export function formatFullDateTime(dateStr: string | null | undefined): string | null {
  if (!dateStr) return null;
  const date = new Date(dateStr);
  if (isNaN(date.getTime())) return null;
  return date.toLocaleString('en-US', {
    month: 'numeric',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  });
}

/**
 * Today (or any Date) as a `YYYY-MM-DD` key in the BROWSER'S LOCAL timezone.
 *
 * Deliberately not `toISOString().split('T')[0]`: that converts to UTC first, so
 * for anyone west of Greenwich it rolls over to tomorrow in the evening. A
 * dispatcher assigning a move at 20:30 Central would stamp the next day's
 * appointment date, which then feeds the appointment saverules and the
 * at-risk / service-failure calculations.
 */
export function toLocalDateKey(d: Date = new Date()): string {
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

/**
 * True when a date/datetime string carries no meaningful clock time — either it is
 * date-only, or its time is exactly midnight.
 *
 * Exists for `invoice.senttoaccountingdate`, which became a DATETIME in migration 487
 * so the invoicing clerks can tell a 9am arrival from a 5pm one. Two kinds of row
 * legitimately sit at 00:00 and must NOT render as "arrived at midnight": every row
 * stamped before the migration (deliberately not backfilled), and a future-dated stamp,
 * which means "available from the START of that day" rather than at a moment.
 */
export function hasNoClockTime(value: string | null | undefined): boolean {
  if (!value) return true;
  const s = String(value).trim();
  if (!/[T ]/.test(s)) return true;                            // date only
  // Trailing Z / ±HH:MM is stripped first: anchoring the midnight test at
  // end-of-string made '2026-09-25T00:00:00Z' read as a real clock time.
  const naive = s.replace(/(?:Z|[+-]\d{2}:?\d{2})$/, '');
  return /[T ]00:00(:00(\.0+)?)?$/.test(naive);               // exactly midnight
}
